﻿import { FC, useState } from 'react';
import { Container, Section, Card, CardTitle, CardDescription, Button, Badge, Alert } from '../components/ui';
import { SEOHead } from '../components/seo';
import {
  getProductBySlug,
  formatPriceINR,
  ATS_RESUME_MASTER_KIT_SLUG,
  LINKEDIN_TOOLKIT_SLUG,
  INTERVIEW_CRACKER_KIT_SLUG,
  AI_JOB_SEARCH_TOOLKIT_SLUG,
  JOB_SWITCH_BLUEPRINT_SLUG
} from '../config/catalog';
import { ROUTES } from '../config/routes';
import { trackProductView, trackBuyClick, trackConsultationClick } from '../analytics/events';
import { PageProps } from './HomePage';

export interface ProductDetailPageProps extends PageProps {
  slug: string;
}

interface ProductDetailMeta {
  problemSolved: string;
  formatDetails: string;
  faqs: { q: string; a: string }[];
  nextProductSlug: string | null;
  nextProductReason: string;
}

const PRODUCT_DETAIL_DATA: Record<string, ProductDetailMeta> = {
  [ATS_RESUME_MASTER_KIT_SLUG]: {
    problemSolved:
      'This kit provides practical resume templates for different career stages, including fresher, experienced professional, and career-switcher resumes. The templates are designed to help you organize your experience, skills, and career information in a clear and professional format.',
    formatDetails: '1 PDF + 3 DOCX files: ATS Resume Master Kit PDF, Fresher Resume Template, Experienced Resume Template, and Career Switch Resume Template',
    faqs: [
      {
        q: 'Are these resume templates ATS-friendly?',
        a: 'Yes. All formats are stripped of problematic parsing elements (such as nested tables, complex text boxes, and multi-column headers) while retaining modern, executive visual appeal for human screeners.'
      },
      {
        q: 'Can I customize these templates in both Microsoft Word and Google Docs?',
        a: 'The three resume templates are provided in editable .docx format. The ATS Resume Master Kit is provided as a PDF.'
      },
      {
        q: 'Is this kit suitable for technical as well as non-technical roles?',
        a: 'Yes. The kit includes modular section structures suitable for software engineers, product managers, marketing specialists, HR professionals, and operational leaders.'
      }
    ],
    nextProductSlug: LINKEDIN_TOOLKIT_SLUG,
    nextProductReason: 'After preparing your resume, you can also improve your LinkedIn profile and professional presence.'
  },

  [LINKEDIN_TOOLKIT_SLUG]: {
    problemSolved:
      'Many candidates have generic LinkedIn headlines, incomplete About sections, and weak keyword relevance. This toolkit provides practical templates and guidance for improving LinkedIn profile positioning, messaging, networking, and content.',
    formatDetails: 'PDF + DOCX files covering LinkedIn headlines, About section, profile optimization, networking, content ideas, and before-vs-after examples',
    faqs: [
      {
        q: 'Do I need LinkedIn Premium to use this toolkit?',
        a: 'No. Every strategy and profile optimization method in this toolkit is engineered for standard, free LinkedIn accounts.'
      },
      {
        q: 'How quickly can I implement these changes?',
        a: 'The step-by-step swipe files allow you to revamp your headline, About section, and experience bullet points in under two hours.'
      },
      {
        q: 'Does it include guidance on networking and reaching out to hiring managers?',
        a: 'Yes. The toolkit includes polite, high-converting direct message templates to connect with hiring managers and recruiters without sounding pushy.'
      }
    ],
    nextProductSlug: INTERVIEW_CRACKER_KIT_SLUG,
    nextProductReason: 'After improving your LinkedIn profile, continue with practical interview preparation and job-search tools.'
  },

  [INTERVIEW_CRACKER_KIT_SLUG]: {
    problemSolved:
      'Preparing for interviews can be difficult when you are unsure how to structure answers, handle HR questions, or approach different stages of the interview process. This kit provides practical interview questions and answers, HR guidance, interview strategy, follow-up resources, answer frameworks, confidence support, and before-vs-after examples.',
    formatDetails: '9 PDF files covering interview questions, HR questions, strategy, follow-up, checklists, answer frameworks, confidence, and examples',
    faqs: [
      {
        q: 'How is this different from the free Interview Preparation Starter Guide?',
        a: 'The free starter guide is a foundational checklist. The paid Interview Cracker Kit is an extensive, in-depth system with 50+ scripted answer frameworks, behavioral question teardowns, and salary negotiation tactics.'
      },
      {
        q: 'Does this include HR and general interview questions?',
        a: 'Yes. It covers leadership principles, conflict resolution, dealing with failure, project management trade-offs, and behavioral STAR models.'
      },
      {
        q: 'Does the kit include follow-up guidance after an interview?',
        a: 'Yes. It includes word-for-word email and phone scripts to counter lowball offers professionally and maximize base pay and perks.'
      }
    ],
    nextProductSlug: AI_JOB_SEARCH_TOOLKIT_SLUG,
    nextProductReason: 'After preparing for interviews, continue your job search with practical AI-assisted application and job-search resources.'
  },

  [AI_JOB_SEARCH_TOOLKIT_SLUG]: {
    problemSolved:
      'Applying to hundreds of jobs manually is exhausting, and generic AI prompts produce robotic, detectable cover letters that get rejected. This toolkit gives you a curated prompt library engineered to perform deep company research, tailor resumes to specific job descriptions with precision, and draft bespoke outreach messages in minutes.',
    formatDetails: '5 PDF files + 1 Excel Job Application Tracker',
    faqs: [
      {
        q: 'Which AI platforms are supported?',
        a: 'All prompts are tested and optimized for ChatGPT (Free & Plus), Claude, and Google Gemini.'
      },
      {
        q: 'Will recruiters detect that I used AI?',
        a: 'No. Our prompts enforce human-like tone, active voice, and authentic storytelling parameters that avoid cliché AI buzzwords.'
      },
      {
        q: 'Does it help with company research before interviews?',
        a: 'Yes. Dedicated prompts extract company challenges, executive priorities, and recent news to help you impress interviewers.'
      }
    ],
    nextProductSlug: JOB_SWITCH_BLUEPRINT_SLUG,
    nextProductReason: 'Organize your entire multi-channel transition into a structured 30-day timeline with daily milestones.'
  },

  [JOB_SWITCH_BLUEPRINT_SLUG]: {
    problemSolved:
      'Job searches drag on for months when professionals lack structured daily goals, application tracking, and an organized timeline. This blueprint provides a disciplined, day-by-day 4-week execution roadmap that balances outbound outreach, skill brush-up, interview scheduling, and pipeline management without causing professional burnout.',
    formatDetails: '7 PDF files + 2 Excel files, including the Daily Checklist and Job Application Tracker',
    faqs: [
      {
        q: 'Can I follow this blueprint while currently employed full-time?',
        a: 'Yes. The system is specifically engineered for working professionals, requiring 45 to 60 focused minutes per weekday plus weekend review blocks.'
      },
      {
        q: 'What tools are required to use the tracker?',
        a: 'The pipeline tracker is provided as a standard spreadsheet compatible with Microsoft Excel and Google Sheets.'
      },
      {
        q: 'What if I am switching domains or changing industries?',
        a: 'The blueprint includes specific guidance for transferrable skill mapping and explaining domain pivots clearly to prospective employers.'
      }
    ],
    nextProductSlug: null,
    nextProductReason: 'For personalized, dedicated mentorship through your career pivot, explore our 1-on-1 Career Coaching programs.'
  }
};

export const ProductDetailPage: FC<ProductDetailPageProps> = ({ slug, onNavigate }) => {
  const product = getProductBySlug(slug);
  const detailData = PRODUCT_DETAIL_DATA[slug];
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  if (!product) {
    return (
      <Section title="Product Not Found">
        <Container size="md">
          <Alert variant="error" title="Resource Unavailable">
            The requested career toolkit could not be found in our catalog.
          </Alert>
          <div style={{ marginTop: '1.5rem' }}>
            <Button variant="outline" onClick={() => onNavigate(ROUTES.DIGITAL_PRODUCTS)}>
              ← Back to Digital Products
            </Button>
          </div>
        </Container>
      </Section>
    );
  }

  const nextProduct = detailData?.nextProductSlug ? getProductBySlug(detailData.nextProductSlug) : null;

  const handleBuyClick = () => {
    trackProductView(product.slug, product.priceINR, 'INR');
    trackBuyClick(product.slug, product.priceINR);
    onNavigate(`${ROUTES.CHECKOUT}?product=${product.slug}`);
  };

  const handleNextProductClick = (nextSlug: string, nextPrice: number) => {
    trackProductView(nextSlug, nextPrice, 'INR');
    trackBuyClick(nextSlug, nextPrice);
    onNavigate(`/digital-products/${nextSlug}`);
  };

  const handleCoachingClick = () => {
    trackConsultationClick('product_detail_next_step', product.slug);
    onNavigate(ROUTES.CAREER_COACHING);
  };

  return (
    <>
      <SEOHead
        title={`${product.title} — Connect Source`}
        description={product.subtitle}
        ogType="product"
      />

      {/* Main Product Overview */}
      <section style={{ backgroundColor: '#ffffff', borderBottom: '1px solid var(--color-border-subtle)', padding: '4rem 0' }}>
        <Container size="xl">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '3.5rem', alignItems: 'start' }}>
            
            {/* Left Column: Product Information & Modules */}
            <div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <Badge variant="primary">{product.deliverableType.replace('_', ' ')}</Badge>
                <Badge variant="neutral">Instant Download</Badge>
              </div>

              <h1 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: 800, color: 'var(--color-navy-dark)', lineHeight: 1.2 }}>
                {product.title}
              </h1>

              <p style={{ fontSize: '1.15rem', color: 'var(--color-text-secondary)', marginTop: '1rem', lineHeight: 1.65 }}>
                {product.subtitle}
              </p>

              {/* The Problem It Solves */}
              <div style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: 'var(--color-bg-canvas)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-subtle)' }}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--color-navy-dark)', marginBottom: '0.5rem' }}>
                  The Problem It Solves:
                </h3>
                <p style={{ fontSize: '0.925rem', color: 'var(--color-text-secondary)', lineHeight: 1.65 }}>
                  {detailData?.problemSolved || product.subtitle}
                </p>
              </div>

              {/* Who It's For */}
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--color-navy-dark)', marginBottom: '0.85rem' }}>
                  Who This Toolkit Is For:
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {product.targetAudience.map((audience, idx) => (
                    <li key={idx} style={{ display: 'flex', gap: '0.6rem', fontSize: '0.925rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                      <span style={{ color: 'var(--color-brand-primary)', fontWeight: 700 }}>✓</span>
                      <span>{audience}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* What's Included */}
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--color-navy-dark)', marginBottom: '0.85rem' }}>
                  What's Included in This Package:
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {product.features.map((feature, idx) => (
                    <li key={idx} style={{ display: 'flex', gap: '0.6rem', fontSize: '0.925rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                      <span style={{ color: 'var(--color-accent-teal)', fontWeight: 700 }}>•</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Format */}
              <div style={{ marginTop: '2rem', padding: '1rem 1.25rem', backgroundColor: '#f0fdf4', borderRadius: 'var(--radius-md)', border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase' }}>
                  Deliverable Format:
                </div>
                <div style={{ fontSize: '0.9rem', color: '#15803d', marginTop: '0.25rem' }}>
                  {detailData?.formatDetails || product.files.map(f => f.fileFormat.toUpperCase()).join(', ')}
                </div>
              </div>
            </div>

            {/* Right Column: Pricing & Purchase Anchor */}
            <div>
              <Card style={{ position: 'sticky', top: '6rem', padding: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
                  <div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
                      Investment
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginTop: '0.35rem' }}>
                      <span style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--color-navy-dark)' }}>
                        {formatPriceINR(product.priceINR)}
                      </span>
                    </div>
                  </div>
                  <Badge variant="success">Instant Access</Badge>
                </div>

                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '1.5rem', lineHeight: 1.4 }}>
                  One-time purchase • Digital product access
                </div>

                <div style={{ borderTop: '1px solid var(--color-border-subtle)', paddingTop: '1.25rem', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ color: 'var(--color-brand-primary)' }}>✓</span> Digital files will be provided after successful payment
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ color: 'var(--color-brand-primary)' }}>✓</span> Zero recurring subscription fees
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ color: 'var(--color-brand-primary)' }}>✓</span> Secure digital delivery
                    </div>
                  </div>
                </div>

                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleBuyClick}
                  style={{ width: '100%' }}
                >
                  {product.ctaText}
                </Button>

                <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => onNavigate(ROUTES.DIGITAL_PRODUCTS)}
                    style={{ background: 'none', border: 'none', fontSize: '0.85rem', color: 'var(--color-text-muted)', cursor: 'pointer' }}
                  >
                    ← Browse all digital toolkits
                  </button>
                </div>
              </Card>
            </div>

          </div>
        </Container>
      </section>

      {/* Product FAQs */}
      {detailData?.faqs && detailData.faqs.length > 0 && (
        <Section title="Frequently Asked Questions" subtitle="Details about file formats, compatibility, and implementation.">
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {detailData.faqs.map((faq, idx) => (
              <div key={idx} className="cs-faq-item">
                <button
                  type="button"
                  className="cs-faq-question"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  aria-expanded={openFaq === idx}
                >
                  <span>{faq.q}</span>
                  <span style={{ fontSize: '1.25rem', color: 'var(--color-brand-primary)' }}>
                    {openFaq === idx ? '−' : '+'}
                  </span>
                </button>
                {openFaq === idx && (
                  <div className="cs-faq-answer">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Related Product Recommendation UI Foundation */}
      <section style={{ backgroundColor: '#ffffff', borderTop: '1px solid var(--color-border-subtle)', padding: '4.5rem 0' }}>
        <Container size="lg">
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ marginBottom: '1.75rem' }}>
              <Badge variant="primary">Recommended Next Step (Optional)</Badge>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--color-navy-dark)', marginTop: '0.5rem' }}>
                Complementary Career Accelerators
              </h2>
              <p style={{ fontSize: '0.95rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
                {detailData?.nextProductReason}
              </p>
            </div>

            {nextProduct ? (
              <Card style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <Badge variant="neutral">{nextProduct.deliverableType.replace('_', ' ')}</Badge>
                    <CardTitle style={{ fontSize: '1.3rem', marginTop: '0.5rem' }}>{nextProduct.title}</CardTitle>
                    <CardDescription style={{ marginTop: '0.35rem', maxWidth: '550px' }}>
                      {nextProduct.subtitle}
                    </CardDescription>
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--color-navy-dark)' }}>
                    {formatPriceINR(nextProduct.priceINR)}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => handleNextProductClick(nextProduct.slug, nextProduct.priceINR)}
                  >
                    Explore {nextProduct.title} →
                  </Button>
                </div>
              </Card>
            ) : (
              /* If at the end of digital sequence (Job Switch Blueprint), optionally recommend Career Coaching */
              <Card style={{ padding: '2rem', backgroundColor: 'var(--color-bg-canvas)' }}>
                <Badge variant="teal">Personalized 1-on-1 Guidance</Badge>
                <CardTitle style={{ fontSize: '1.3rem', marginTop: '0.5rem' }}>
                  Ready for 1-on-1 Career Coaching?
                </CardTitle>
                <CardDescription style={{ marginTop: '0.5rem', lineHeight: 1.6 }}>
                  If you need direct, personalized accountability, mock interviews with structured feedback, and tailored career strategy, explore our 1-on-1 coaching programs.
                </CardDescription>
                <div style={{ marginTop: '1.5rem' }}>
                  <Button variant="primary" size="md" onClick={handleCoachingClick}>
                    Explore Coaching Programs
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </Container>
      </section>
    </>
  );
};














